import { oneSecondInMilliseconds } from "constants/time/oneSecondInMilliseconds";

import React, { useEffect, useState } from "react";

import { Button } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Mode } from "app/pages/reports-and-analytics/types/client";
import ExcelIcon from "assets/ExcelIcon";
import useDocumentVisibility from "hooks/useDocumentVisibility";
import { SnackbarKey, useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";
import { downloadBlob } from "utils/downloadBlob";

import { useExportBrokerReportCsvMutation, useGetBrokerCsvForDownloadQuery } from "../api/brokerInsightsApi";
import { useBrokerInsightsIndexSliceSelector } from "../store/brokerInsightsIndexStore";

const BrokerInsightsExportView: React.VFC = () => {
  const { t } = useTranslation("common");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isDocumentVisible = useDocumentVisibility();

  const [jobId, setJobId] = useState<number>();
  const [snackbarId, setSnackbarId] = useState<SnackbarKey>();
  const [isLoading, setIsLoading] = useState(false);

  // const { reportId, mode, from, to, selectedOwners, selectedLane, searchInput } = useInsightsV2SliceSelector(
  //   (store) => store.insightsV2UiSlice
  // );

  const {
    fromDate: from,
    toDate: to,
    selectedReport,
    searchQuery,
    mode,
  } = useBrokerInsightsIndexSliceSelector((store) => store.brokerInsightsIndexFilterSlice);

  const reportId = selectedReport?.id ?? -1;

  const [exportReportsCsv] = useExportBrokerReportCsvMutation();
  const { data } = useGetBrokerCsvForDownloadQuery(
    jobId && selectedReport?.id ? { urlParams: { jobId, reportId: selectedReport?.id } } : skipToken,
    {
      pollingInterval: isDocumentVisible ? oneSecondInMilliseconds * 5 : undefined,
    }
  );

  const handleOnClick = async () => {
    setIsLoading(true);
    try {
      const { jobId } = await exportReportsCsv({
        urlParams: { reportId },
        queryParams: {
          mode: mode as unknown as Mode,
          from,
          to,
          search: searchQuery,
        },
      }).unwrap();
      setJobId(jobId);
      setSnackbarId(enqueueSnackbar(t("exportCsv_inProgress"), { variant: "warning" }));
    } catch (e) {
      enqueueSnackbar(t("errors.generic"), { variant: "error" });
      Sentry.captureException(e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!!data && !!data.csv) {
      const { csv } = data;
      const blob = new Blob([csv], { type: "text/csv" });
      const fileName = `Generated_Report.csv`;
      downloadBlob(blob, fileName);

      setJobId(undefined);
      closeSnackbar(snackbarId);
      setSnackbarId(undefined);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Button
      variant="outlined"
      style={{ color: "rgb(0, 109, 69)" }}
      startIcon={<ExcelIcon width={26} height={26} />}
      loading={isLoading}
      onClick={handleOnClick}
    >
      {t("exportCsv")}
    </Button>
  );
};

export default BrokerInsightsExportView;
